import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { PredefinedMilisecondsTypeEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { StockExchangeCodeType } from '../../lib/models/fundamental/types';
import { StockDescriptor } from '../../lib/models/stock-descriptor';
import { addOrRemoveSymbolToFavoriteList } from '../../lib/store/common-actions';
import {
  DEFAULT_SEARCH_STOCK_LIMIT,
  SEARCH_STOCK_MAX_LIMIT,
} from '../../lib/store/fundamental/constants';
import { getCompanyLogoAndName } from '../../lib/store/fundamental/index';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { isSymbolInFavourites } from '../../lib/store/helpers';
import { RootState } from '../../lib/store/index';
import { SymbolFavoriteListActionType, SymbolLite } from '../../lib/store/market-data/types';
import { getFavoriteStocks } from '../../lib/store/selectors';
import { defaultLogoBase64 } from '../../lib/util/assets/logos';
import { FAVOURITE_SYMBOLS_MAX_LIMIT } from '../../lib/util/constants';
import {
  isCallStatusError,
  isCallStatusPending,
} from '../../lib/util/error-handling/StatusByCallHelpers';

import ErrorIcon from '../../assets/img/CrashIcon.png';
import SearchIcon from '../../assets/img/search.png';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import FavouriteSymbolsLimitModal from '../FavouriteSymbolsLimitModal/FavouriteSymbolsLimitModal';
import { Preloader } from '../index';
import ResultRowMobile from '../SearchSymbolsMob/Search/ResultRow';

import FavouritesModal from './componets/FavouritesModal';

import styles from './StocksResult.module.scss';

type SearchStockDescriptor = StockDescriptor & {
  stockExchangeCode?: StockExchangeCodeType;
  countryDesignation?: string;
};

/**
 * useDelayedFalse
 *
 * Keeps a boolean “true” state on for at least `delay` milliseconds
 * after the input flag goes false—so that short-lived gaps (like
 * between search completion and logo‑fetch start) don’t hide the spinner
 * and cause a visual flicker.
 */
function useDelayedFalse(flag: boolean, delay = PredefinedMilisecondsTypeEnum.halfSecond): boolean {
  const [show, setShow] = useState(flag);

  useEffect(() => {
    let delayTimeout: NodeJS.Timeout;

    if (flag) {
      setShow(true);
    } else {
      delayTimeout = setTimeout(() => setShow(false), delay);
    }

    return () => {
      if (delayTimeout) {
        clearTimeout(delayTimeout);
      }
    };
  }, [flag, delay]);

  return show;
}

type Props = {
  noSymbolsFoundMessage: string;
  currentPageNumber: number;
  handleLoadMore: () => void;
  closeModal?: () => void;
  isMob?: boolean;
};

const StocksResult: React.FC<Props> = ({
  noSymbolsFoundMessage,
  currentPageNumber,
  isMob,
  closeModal,
  handleLoadMore,
}) => {
  const dispatch = useDispatch();

  const stocks = useSelector<RootState, SearchStockDescriptor[]>(
    s => s.fundamental.stockResults,
  );
  const query = useSelector<RootState, string>(
    s => s.fundamental.stockQuery,
  );

  const isSearchPending = useSelector<RootState, boolean>(s =>
    isCallStatusPending(FundamentalCall.searchStocks, s.fundamental.statusByCall));
  const isSearchError = useSelector<RootState, boolean>(s =>
    isCallStatusError(FundamentalCall.searchStocks, s.fundamental.statusByCall));

  const isLogoFetchPending = useSelector<RootState, boolean>(s =>
    isCallStatusPending(FundamentalCall.getCompanyLogoAndName, s.fundamental.statusByCall));

  const isLoading = isSearchPending || isLogoFetchPending;
  const showSpinner = useDelayedFalse(isLoading, PredefinedMilisecondsTypeEnum.halfSecond);

  const userIdOrCorrelationId = useSelector<RootState, number | undefined>(
    s => s.crm.individualExtendedInfo?.user_correlation_id,
  );
  const favourites = useSelector<RootState, SymbolLite[]>(s =>
    getFavoriteStocks(s));
  const companyLogoAndNameData = useSelector<RootState, Record<string, { logo?: string; url?: string }>>(
    s => s.fundamental.companyLogoAndName,
  );

  const [isAddedToFavourites, setIsAddedToFavourites] = useState(false);
  const [isRemovedFromFavourites, setIsRemovedFromFavourites] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [showFavouritesLimitMessage, setShowFavouritesLimitMessage] = useState(false);

  const filteredResults = stocks.filter(stock => {
    const uppercaseQuery = query.toUpperCase();
    return (
      (stock.symbol && stock.symbol.startsWith(uppercaseQuery))
      || (stock.companyName && stock.companyName.toUpperCase().startsWith(uppercaseQuery))
    );
  });

  useEffect(() => {
    if (!isSearchPending) {
      const start = (currentPageNumber - 1) * DEFAULT_SEARCH_STOCK_LIMIT;
      const pageSyms = filteredResults
        .map(stock => stock.symbol)
        .slice(start, start + DEFAULT_SEARCH_STOCK_LIMIT)
        .join(',');
      if (pageSyms) dispatch(getCompanyLogoAndName(pageSyms));
    }
  }, [dispatch, isSearchPending, filteredResults, currentPageNumber]);

  const handleEndReached = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (!isSearchPending) handleLoadMore();
  };

  const onAddToFavourites = useCallback((
    symbol: string,
    stockExchangeCode?: StockExchangeCodeType,
    countryDesignation?: string,
  ) => {
    setSelectedSymbol(symbol);
    if (!userIdOrCorrelationId) {
      return;
    }

    if (isSymbolInFavourites(favourites, symbol)) {
      dispatch(
        addOrRemoveSymbolToFavoriteList({
          symbolAndExchange: { symbol, stockExchangeCode, countryDesignation },
          actionType: SymbolFavoriteListActionType.Remove,
          userCorrelationId: userIdOrCorrelationId,
        }),
      );
      setIsRemovedFromFavourites(true);
      setIsAddedToFavourites(false);
    } else if (favourites.length >= FAVOURITE_SYMBOLS_MAX_LIMIT) {
      setShowFavouritesLimitMessage(true);
    } else {
      dispatch(
        addOrRemoveSymbolToFavoriteList({
          symbolAndExchange: { symbol, stockExchangeCode, countryDesignation },
          actionType: SymbolFavoriteListActionType.Add,
          userCorrelationId: userIdOrCorrelationId,
        }),
      );
      setIsAddedToFavourites(true);
    }
  }, [dispatch, favourites, userIdOrCorrelationId]);

  const closeFavouritesModal = () => {
    setIsAddedToFavourites(false);
    setIsRemovedFromFavourites(false);
    setShowFavouritesLimitMessage(false);
  };

  const renderStockResults = () => {
    if (!query) {
      return (
        <div className={classNames(styles.iconWrapper, { [styles.searchIconMob]: isMob })}>
          <img src={SearchIcon} alt="looking for symbol" />
          <p className={styles.text}>{lang.mobileStockSearchDescription()}</p>
        </div>
      );
    }

    if (showSpinner) {
      return (
        <div className={isMob ? styles.mobLoader : styles.webLoader}>
          <Preloader isFullScreen owner="StockResult" />
        </div>
      );
    }

    if (isSearchError) {
      return (
        <div className={classNames(styles.iconWrapper, { [styles.searchIconMob]: isMob })}>
          <img src={ErrorIcon} alt="error searching symbol" />
          <p className={styles.text}>{lang.requestErrorOver500()}</p>
        </div>
      );
    }

    if (!filteredResults.length) {
      return (
        <div className={classNames(styles.noSymbols, { [styles.noSymbolsMob]: isMob })}>
          {noSymbolsFoundMessage}
        </div>
      );
    }

    return filteredResults.map(stock => (
      <ResultRowMobile
        key={stock.symbol}
        stock={stock}
        logo={companyLogoAndNameData[stock.symbol]?.logo ?? defaultLogoBase64}
        url={companyLogoAndNameData[stock.symbol]?.url}
        addToFavourites={() => onAddToFavourites(
          stock.symbol,
          stock.stockExchangeCode,
          stock.countryDesignation,
        )}
        closeModal={closeModal ?? (() => { })}
      />
    ));
  };

  return (
    <div className={classNames(styles.resultWrapper, { [styles.resultWrapperMob]: isMob })}>
      {renderStockResults()}

      {filteredResults.length === DEFAULT_SEARCH_STOCK_LIMIT * currentPageNumber
        && filteredResults.length <= SEARCH_STOCK_MAX_LIMIT
        && (
          <Button
            className={styles.btnLoadMore}
            variant={ButtonTypesEnum.Link}
            onClick={handleEndReached}
          >
            {lang.commonLoadMore()}
          </Button>
        )}

      {(isAddedToFavourites || isRemovedFromFavourites) && (
        <FavouritesModal
          isAddedToFavourites={isAddedToFavourites}
          isRemovedFromFavourites={isRemovedFromFavourites}
          symbol={selectedSymbol}
          closeModal={closeFavouritesModal}
          isMobile={Boolean(isMob)}
        />
      )}

      {showFavouritesLimitMessage && (
        <FavouriteSymbolsLimitModal
          closeMessage={() => setShowFavouritesLimitMessage(false)}
          isMobile={Boolean(isMob)}
        />
      )}
    </div>
  );
};

export default StocksResult;
